import { useRef } from "react";
import {
    Globe,
    Smartphone,
    Database,
    Brain,
    Palette,
    Server,
    HeadphonesIcon,
} from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useTranslation } from "react-i18next";

interface Service {
    num: string;
    icon: React.ReactNode;
    key: string;
    tags?: string[];
    accentHue: number; // HSL hue for accent color
}

const SERVICES: Service[] = [
    {
        num: "01",
        icon: <Globe className="w-8 h-8" />,
        key: "web",
        tags: ["REACT", "NEXT.JS", "POSTGRES"],
        accentHue: 260,
    },
    {
        num: "02",
        icon: <Smartphone className="w-8 h-8" />,
        key: "mobile",
        tags: ["iOS", "ANDROID", "FLUTTER"],
        accentHue: 190,
    },
    {
        num: "03",
        icon: <Database className="w-8 h-8" />,
        key: "erp",
        tags: ["ERP", "CRM", "ANALYTICS"],
        accentHue: 280,
    },
    {
        num: "04",
        icon: <Brain className="w-8 h-8" />,
        key: "ai",
        tags: ["OPENAI", "CLAUDE", "RAG"],
        accentHue: 220,
    },
    {
        num: "05",
        icon: <Palette className="w-8 h-8" />,
        key: "branding",
        tags: ["BRANDING", "LOGO", "UI"],
        accentHue: 320,
    },
    {
        num: "06",
        icon: <Server className="w-8 h-8" />,
        key: "hosting",
        tags: ["CLOUD", "SSL", "CDN"],
        accentHue: 160,
    },
    {
        num: "07",
        icon: <HeadphonesIcon className="w-8 h-8" />,
        key: "support",
        tags: ["24/7", "SLA", "tag_partnership"],
        accentHue: 30,
    },
];

const ServiceCard = ({ service }: { service: Service }) => {
    const { t } = useTranslation();
    const accent = `hsl(${service.accentHue} 90% 60%)`;
    const accentSoft = `hsl(${service.accentHue} 90% 60% / 0.18)`;

    return (
        <div
            className="glass-v2 hover-lift-v2 rounded-[32px] p-8 md:p-10 group relative overflow-hidden"
            data-cursor="hover"
        >
            {/* Hover radial glow, service-tinted */}
            <div
                aria-hidden
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{
                    background: `radial-gradient(500px circle at 50% 0%, ${accentSoft}, transparent 65%)`,
                }}
            />

            {/* Subtle top accent line */}
            <div
                aria-hidden
                className="absolute top-0 inset-x-8 h-px opacity-40 pointer-events-none"
                style={{
                    background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
                }}
            />

            <div className="relative">
                <div className="flex items-start gap-5 mb-5">
                    {/* Accented icon block */}
                    <div
                        className="w-16 h-16 shrink-0 rounded-2xl flex items-center justify-center text-white transition-transform duration-500 group-hover:scale-110"
                        style={{
                            background: `linear-gradient(145deg, ${accent}, hsl(${service.accentHue} 90% 45%))`,
                            boxShadow: `0 0 32px ${accentSoft}`,
                        }}
                    >
                        {service.icon}
                    </div>

                    <div className="flex-1 min-w-0 pt-1">
                        <p className="font-v2-label text-[10px] text-white/40 mb-1.5">
                            {t("services.step_label", { num: service.num })}
                        </p>
                        <h3 className="font-v2-display text-2xl md:text-3xl text-white leading-tight">
                            {t(`services.items.${service.key}.title`)}
                        </h3>
                    </div>
                </div>

                <p className="text-base md:text-lg text-muted-foreground leading-relaxed font-medium">
                    {t(`services.items.${service.key}.desc`)}
                </p>

                {service.tags && service.tags.length > 0 && (
                    <div className="mt-6 flex flex-wrap gap-2">
                        {service.tags.map((tag) => (
                            <span
                                key={tag}
                                className="font-v2-label text-[10px] px-3 py-1 rounded-full border transition-colors"
                                style={{
                                    background: accentSoft,
                                    borderColor: `hsl(${service.accentHue} 90% 60% / 0.3)`,
                                    color: `hsl(${service.accentHue} 90% 85%)`,
                                }}
                            >
                                {tag === "tag_partnership" ? t("services.items.tag_partnership") : tag}
                            </span>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const ServiceRow = ({ service, index }: { service: Service; index: number }) => {
    const isEven = index % 2 === 0; // 0,2,4,6 → right; 1,3,5 → left
    const accent = `hsl(${service.accentHue} 90% 60%)`;

    return (
        <motion.li
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{
                duration: 0.8,
                ease: [0.23, 1, 0.32, 1],
                delay: Math.min(index * 0.04, 0.2),
            }}
            className="relative md:grid md:grid-cols-2 md:gap-12 lg:gap-16 items-center"
        >
            {/* Center node on spine — absolute so it stays over the spine */}
            <div
                aria-hidden
                className="hidden md:flex absolute right-1/2 top-10 translate-x-1/2 w-14 h-14 rounded-full items-center justify-center z-10"
                style={{
                    background: "var(--gradient-accent-v2)",
                    boxShadow: `0 0 32px ${accent}, 0 0 60px hsl(260 90% 65% / 0.3)`,
                }}
            >
                <span className="font-v2-label text-[11px] text-white">{service.num}</span>
            </div>

            {/* Connector stub — horizontal line from spine toward the card */}
            <div
                aria-hidden
                className={`hidden md:block absolute top-[66px] h-px w-10 lg:w-14 ${isEven ? "right-1/2 mr-7" : "left-1/2 ml-7"}`}
                style={{
                    background: `linear-gradient(${isEven ? "270deg" : "90deg"}, ${accent}, transparent)`,
                    opacity: 0.6,
                }}
            />

            {/* Card placed on right (even) or left (odd) column */}
            <div
                className={`${isEven ? "md:col-start-1" : "md:col-start-2"}`}
            >
                <ServiceCard service={service} />
            </div>
        </motion.li>
    );
};

const ServicesTimeline = () => {
    const { t } = useTranslation();
    const containerRef = useRef<HTMLDivElement>(null);
    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start end", "end start"],
    });
    const spineScale = useTransform(scrollYProgress, [0.1, 0.9], [0, 1]);

    return (
        <section
            ref={containerRef}
            className="py-20 md:py-32 relative"
            id="services"
        >
            <div className="container mx-auto px-6 relative">
                {/* Header */}
                <div className="max-w-3xl mx-auto text-center mb-20">
                    <p className="font-v2-label text-xs text-primary/80 mb-4">{t("services.eyebrow")}</p>
                    <h2 className="font-v2-display text-4xl md:text-6xl text-white mb-6">
                        {t("services.title_prefix")}{" "}
                        <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                            {t("services.title_highlight")}
                        </span>
                    </h2>
                    <p className="text-lg text-muted-foreground leading-relaxed font-medium">
                        {t("services.subtitle")}
                    </p>
                </div>

                {/* Timeline */}
                <div className="relative max-w-6xl mx-auto">
                    {/* Base spine */}
                    <div
                        aria-hidden
                        className="hidden md:block absolute right-1/2 top-4 bottom-4 w-px translate-x-px bg-white/[0.06]"
                    />
                    {/* Progressive violet→cyan fill on scroll */}
                    <motion.div
                        aria-hidden
                        className="hidden md:block absolute right-1/2 top-4 bottom-4 w-px translate-x-px origin-top"
                        style={{
                            scaleY: spineScale,
                            background:
                                "linear-gradient(180deg, hsl(260 90% 65% / 0.9), hsl(190 90% 50% / 0.9), hsl(260 90% 65% / 0.4))",
                        }}
                    />

                    <ol className="space-y-14 md:space-y-16">
                        {SERVICES.map((s, i) => (
                            <ServiceRow key={s.num} service={s} index={i} />
                        ))}
                    </ol>
                </div>
            </div>
        </section>
    );
};

export default ServicesTimeline;
